import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../../services/helpers";
import ToolTip from "../../components/component-tools/ToolTip";
import ConfirmPopup from "../../components/client-components/ConfirmPopup";
import { useAuth } from "../../auth";
import { getClientByEmail } from "../../services/clientService";
import {
  getActiveSubscriptionDetailsByClient,
  stopSubscriptionDetail,
} from "../../services/subscriptionDetailService";
import { BiggerSubscriptionDetail } from "../../interfaces/subscriptionDetail";
import NotificationsPopup from "../../components/client-components/NotificationsPopup";
import { ClientNotification } from "../../interfaces/clientNotification";
import { Client } from "../../interfaces/client";
import { getNotifications } from "../../services/notificationsService";

const SubscriptionOrdersClient = () => {
  const [client, setClient] = useState<Client | null>(null);
  const { decodeToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscriptionDetails, setSubscriptionDetails] = useState<
    BiggerSubscriptionDetail[]
  >([]);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [
    showConfirmationStopSubscription,
    setShowConfirmationStopSubscription,
  ] = useState(false);
  const [selectedSubscriptionDetailID, setSelectedSubscriptionDetailID] =
    useState<number | null>(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [notifications, setNotifications] = useState<ClientNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const email = decodeToken(localStorage.getItem("token") || "");
        const clientIDString = localStorage.getItem("clientID");
        const clientID = clientIDString ? parseInt(clientIDString, 10) : 0;
        const client = await getClientByEmail(email, clientID);
        setClient(client);

        const detailsResponse = await getActiveSubscriptionDetailsByClient(
          client.clientID,
        );
        setSubscriptionDetails(detailsResponse);

        const notificationsResponse = await getNotifications(client?.clientID);
        notificationsResponse.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        setNotifications(notificationsResponse);

        const unreadCount = notificationsResponse.length;
        setUnreadCount(unreadCount);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredDetails = subscriptionDetails
    .filter((detail): detail is NonNullable<typeof detail> => detail !== null)
    .filter(
      (detail) =>
        detail.candidateFirstName
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        detail.candidateLastName
          .toLowerCase()
          .includes(searchTerm.toLowerCase()),
    );

  const handleStopSubscription = async () => {
    if (!selectedSubscriptionDetailID) return;

    setShowConfirmationStopSubscription(false);
    setLoading(true);

    try {
      await stopSubscriptionDetail(selectedSubscriptionDetailID);

      setSubscriptionDetails((prevDetails) =>
        prevDetails.filter(
          (x) => x.subscriptionDetailID !== selectedSubscriptionDetailID,
        ),
      );

      setSuccessMessage("Stopped Subscription.");
    } catch (error) {
      console.error("Error stopping subscription:", error);
      alert("An error occurred while saving the form. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="content-container">
      <div className="header-container">
        <h1 style={{ display: "flex" }}>
          Subscription Candidates
          <ToolTip
            tooltipText="All orders that have a subscription service active."
            placement="right"
            className="info-icon"
          />
        </h1>

        <div className="page-items-container">
          <div className="search-container">
            <input
              style={{ border: "none", width: "100%", outline: "none" }}
              type="text"
              placeholder="Search Candidates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm.trim() ? (
              <i
                className="bi bi-x-circle clear-icon"
                onClick={() => setSearchTerm("")}
                style={{ cursor: "pointer" }}
              ></i>
            ) : (
              <i className="bi bi-search search-icon"></i>
            )}
          </div>
          <button
            className="notification-button"
            onClick={() => setShowNotifications(true)}
          >
            <i className="bi bi-bell-fill"></i>
            {unreadCount > 0 && (
              <span className="notification-dot">{unreadCount}</span>
            )}
          </button>
        </div>
        {showNotifications && client?.clientID && (
          <NotificationsPopup
            notificationsInput={notifications}
            clientID={client?.clientID}
            onClose={(
              newUnreadCount = unreadCount,
              newNotifications = notifications,
            ) => {
              setNotifications(newNotifications);
              setShowNotifications(false);
              setUnreadCount(newUnreadCount);
            }}
          />
        )}
      </div>

      {loading ? (
        <div className="loading-divider">
          <div className="loading-bar"></div>
        </div>
      ) : (
        <>
          <div className="divider"></div>
          <div className="error">
            <b>
              Stop subscription for a candidate by the 25th of the month, in
              order to avoid the next billing cycle.
            </b>
          </div>
          <div className="table-wrapper">
            <div className="table-container">
              <table
                className="table"
                style={{
                  cursor: "pointer",
                }}
              >
                <thead>
                  <tr>
                    <th style={{ width: "150px" }}>Candidate Name</th>
                    <th style={{ width: "70px" }}>Subscription Start</th>
                    <th style={{ width: "100px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDetails?.length === 0 ? (
                    <tr>
                      <td colSpan={3}>
                        No candidates currently have an active subscription
                        service.
                      </td>
                    </tr>
                  ) : (
                    filteredDetails?.map((detail) => {
                      return (
                        <tr
                          key={detail?.subscriptionDetailID}
                          onClick={() =>
                            navigate(`/client/view-order/${detail.orderID}`)
                          }
                        >
                          <td>
                            {detail.candidateFirstName}{" "}
                            {detail.candidateLastName}
                          </td>
                          <td>{formatDate(detail?.initiatedDate, "us")}</td>
                          <td>
                            {detail.stopDate == null && (
                              <div style={{ alignSelf: "end" }}>
                                <button
                                  className="smbtn btn-red"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedSubscriptionDetailID(
                                      detail.subscriptionDetailID,
                                    );
                                    setShowConfirmationStopSubscription(true);
                                  }}
                                >
                                  Stop Subscription
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
      {showConfirmationStopSubscription && (
        <ConfirmPopup
          onConfirm={() => {
            handleStopSubscription();
          }}
          onCancel={() => setShowConfirmationStopSubscription(false)}
          message="Are you sure you want to stop this subscription for this order?"
        />
      )}
      {successMessage && (
        <div className="alert-message alert alert-success alert-dismissible fade show">
          {successMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setSuccessMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}
    </div>
  );
};

export default SubscriptionOrdersClient;
